// src/components/JumpIndicatorsRenderer.tsx
/**
 * Phase 2: Jump Indicator Layout Fix - Renderer Component
 * 
 * Renders jump indicators with:
 * - Horizontal side-by-side layout for sequential chains
 * - Vertical lane stacking for non-chained jumps
 * - Primary and secondary line support
 * 
 * Based on DNL Enhancement Specification v1.0, Section 3.3
 */

import React, { useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '../types/redux';
import type { Jump, JumpChain, JumpLayoutResult, JumpLayoutSettings } from '../types/jump';
import {
  calculateJumpLayout,
  getLaneOffset,
  getJumpDisplayLabel,
  formatJumpLabelForKaTeX,
  getJumpPositionInChain,
} from '../lib/jumpLayout';
import { setJumpSelected, toggleJumpLabelVisibility } from '../store/slices/jumpIndicatorsSlice';
import { getDragCursor } from '../lib/jumpDrag';
import { selectLocale } from '../store/slices/preferencesSlice';
import MathRenderer from './MathRenderer';

// =============================================================================
// TYPES
// =============================================================================

export interface JumpIndicatorsRendererProps {
  /** Which line to render jumps for */
  line: 'primary' | 'secondary';
  /** Whether the number line is horizontal */
  isHorizontal: boolean;
  /** Y position of the line (for horizontal) or X position (for vertical) */
  linePosition: number;
  /** Function to convert values to pixel positions */
  valueToPixel: (value: number) => number;
  /** Dark mode */
  isDark?: boolean;
  /** Handler for starting jump drag operations */
  onDragStart?: (event: React.MouseEvent | React.TouchEvent, jumpId: string, handleType: 'start' | 'end' | 'whole') => void;
  /** Function to check if a jump is currently being dragged */
  isJumpDragging?: (jumpId: string, handleType?: 'start' | 'end' | 'whole') => boolean;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const JUMP_ARC_HEIGHT = 30;
const CHAIN_CONNECTOR_HEIGHT = 15; // Lower arc for chained jumps
const LABEL_OFFSET_Y = 8;
const HANDLE_RADIUS = 5;
const ARROWHEAD_SIZE = 10; // Increased from 8 for better visibility

// Colors
const SELECTION_COLOR = '#3b82f6'; // blue-500
const CHAIN_INDICATOR_COLOR = '#8b5cf6'; // violet-500

// =============================================================================
// COMPONENT
// =============================================================================

export const JumpIndicatorsRenderer: React.FC<JumpIndicatorsRendererProps> = ({
  line,
  isHorizontal,
  linePosition,
  valueToPixel,
  isDark = false,
  onDragStart,
  isJumpDragging,
}) => {
  const dispatch = useDispatch();
  
  // Get jumps and settings from Redux
  const allJumps = useSelector((state: RootState) => state.jumpIndicators.jumps);
  const layoutSettings = useSelector((state: RootState) => state.jumpIndicators.layoutSettings);
  const numberType = useSelector((state: RootState) => state.numberLines.numberType);
  const fractionDisplay = useSelector((state: RootState) => state.numberLines.fractionDisplay);
  const locale = useSelector(selectLocale);
  const textScale = useSelector((state: RootState) => state.preferences.textScale ?? 1);
  
  // Filter jumps for this line
  const jumps = useMemo(
    () => allJumps.filter(j => j.line === line),
    [allJumps, line]
  );
  
  // Calculate layout
  const layoutResult = useMemo(
    () => calculateJumpLayout(jumps, valueToPixel, layoutSettings),
    [jumps, valueToPixel, layoutSettings]
  );
  
  // Handle jump selection
  const handleJumpClick = useCallback((jumpId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    // Selection is now handled by the parent component's drag/drop logic (handleDocumentMouseUp in NumberLine.tsx)
    // to correctly distinguish between clicks and drags.
    // const jump = jumps.find(j => j.id === jumpId);
    // if (jump) {
    //   dispatch(setJumpSelected({ id: jumpId, selected: !jump.selected }));
    // }
  }, []);
  
  if (jumps.length === 0) return null;
  
  return (
    <g className={`jump-indicators jump-indicators-${line}`}>
      {/* Render chain connectors first (behind arcs) */}
      {layoutSettings.chainSequentialJumps && layoutSettings.showChainIndicators && layoutResult.chains.map(chain => (
        <ChainConnector
          key={chain.id}
          chain={chain}
          isHorizontal={isHorizontal}
          linePosition={linePosition}
          valueToPixel={valueToPixel}
          laneOffset={layoutSettings.laneOffset}
        />
      ))}
      
      {/* Render individual jumps */}
      {layoutResult.jumpsWithLanes.map(jump => (
        <JumpIndicator
          key={jump.id}
          jump={jump}
          chains={layoutResult.chains}
          isHorizontal={isHorizontal}
          linePosition={linePosition}
          valueToPixel={valueToPixel}
          layoutSettings={layoutSettings}
          onClick={handleJumpClick}
          isDark={isDark}
          onDragStart={onDragStart}
          isJumpDragging={isJumpDragging}
          numberType={numberType}
          fractionDisplay={fractionDisplay}
          locale={locale}
          textScale={textScale}
          line={line}
        />
      ))}
    </g>
  );
};

// =============================================================================
// CHAIN CONNECTOR
// =============================================================================

interface ChainConnectorProps {
  chain: JumpChain;
  isHorizontal: boolean;
  linePosition: number;
  valueToPixel: (value: number) => number;
  laneOffset: number;
}

/**
 * Visual connector showing that jumps are part of a chain
 * Rendered as a subtle line or bracket under the chained jumps
 */
const ChainConnector: React.FC<ChainConnectorProps> = ({
  chain,
  isHorizontal,
  linePosition,
  valueToPixel,
  laneOffset,
}) => {
  if (chain.jumps.length < 2) return null;
  
  const firstJump = chain.jumps[0];
  const lastJump = chain.jumps[chain.jumps.length - 1];
  
  const startPx = valueToPixel(firstJump.startValue);
  const endPx = valueToPixel(lastJump.endValue);
  const y = linePosition - getLaneOffset(chain.lane, laneOffset) - JUMP_ARC_HEIGHT - 20;
  
  // Create bracket path
  const bracketHeight = 6;
  const path = isHorizontal
    ? `M ${startPx} ${y + bracketHeight} 
       L ${startPx} ${y} 
       L ${endPx} ${y} 
       L ${endPx} ${y + bracketHeight}`
    : `M ${y + bracketHeight} ${startPx} 
       L ${y} ${startPx} 
       L ${y} ${endPx} 
       L ${y + bracketHeight} ${endPx}`;
  
  return (
    <g className="chain-connector">
      <path
        d={path}
        fill="none"
        stroke={CHAIN_INDICATOR_COLOR}
        strokeWidth={1.5}
        strokeDasharray="4,2"
        opacity={0.5}
      />
      {/* Chain label */}
      <text
        x={isHorizontal ? (startPx + endPx) / 2 : y - 5}
        y={isHorizontal ? y - 4 : (startPx + endPx) / 2}
        textAnchor="middle"
        fontSize={10}
        fill={CHAIN_INDICATOR_COLOR}
        opacity={0.7}
      >
        chain ({chain.jumps.length})
      </text>
    </g>
  );
};

// =============================================================================
// JUMP INDICATOR
// =============================================================================

interface JumpIndicatorProps {
  jump: Jump & { lane: number };
  chains: JumpChain[];
  isHorizontal: boolean;
  linePosition: number;
  valueToPixel: (value: number) => number;
  layoutSettings: JumpLayoutSettings;
  onClick: (jumpId: string, event: React.MouseEvent) => void;
  isDark: boolean;
  onDragStart?: (event: React.MouseEvent | React.TouchEvent, jumpId: string, handleType: 'start' | 'end' | 'whole') => void;
  isJumpDragging?: (jumpId: string, handleType?: 'start' | 'end' | 'whole') => boolean;
  numberType: 'integer' | 'decimal' | 'fraction' | 'percent';
  fractionDisplay: { format: 'mixed' | 'improper'; simplify: boolean };
  locale: string;
  textScale: number;
  line: 'primary' | 'secondary';
}

const JumpIndicator: React.FC<JumpIndicatorProps> = ({
  jump,
  chains,
  isHorizontal,
  linePosition,
  valueToPixel,
  layoutSettings,
  onClick,
  isDark,
  onDragStart,
  isJumpDragging,
  numberType,
  fractionDisplay,
  locale,
  textScale,
  line,
}) => {
  const dispatch = useDispatch();
  const startPx = valueToPixel(jump.startValue);
  const endPx = valueToPixel(jump.endValue);
  const width = Math.abs(endPx - startPx);
  
  // Determine if this jump is in a chain
  const chainPosition = getJumpPositionInChain(jump, chains);
  const isInChain = (chainPosition?.chainLength ?? 0) > 1;
  const isFirstInChain = chainPosition?.positionInChain === 0;
  const isLastInChain = chainPosition?.positionInChain === (chainPosition?.chainLength ?? 1) - 1;
  
  // Calculate Y position based on lane
  const laneY = getLaneOffset(jump.lane, layoutSettings.laneOffset);
  // For secondary lines, add extra offset to avoid clashing with tick labels above
  const secondaryLineOffset = line === 'secondary' ? 30 : 0;
  const baseY = isHorizontal 
    ? linePosition - 40 - laneY - secondaryLineOffset 
    : linePosition + 40 + laneY + (line === 'secondary' ? 30 : 0);
  
  // Arc height - lower for chained jumps to make them feel connected
  const arcHeight = isInChain 
    ? Math.min(CHAIN_CONNECTOR_HEIGHT, width * 0.3) 
    : Math.min(layoutSettings.defaultArcHeight, width * 0.5);
  
  // Zero-length jump detection
  const isZeroLength = Math.abs(jump.endValue - jump.startValue) < 0.001;
  
  // Create arc path
  const arcPath = createJumpArcPath(startPx, endPx, baseY, arcHeight, isHorizontal, isZeroLength);
  
  // Calculate arrowhead position at middle of curve (t=0.5)
  // For quadratic bezier Q(P0, P1, P2), point at t=0.5 is:
  // P(t) = (1-t)²P0 + 2(1-t)tP1 + t²P2
  // At t=0.5: P(0.5) = 0.25*P0 + 0.5*P1 + 0.25*P2
  const midX = (startPx + endPx) / 2;
  const controlY = baseY - arcHeight;
  
  // Calculate actual point on curve at t=0.5, then move slightly higher (t=0.48)
  // P0 = (startPx, baseY), P1 = (midX, controlY), P2 = (endPx, baseY)
  // Use t=0.48 to position arrow slightly higher on the curve
  const t = 0.48;
  const oneMinusT = 1 - t;
  const midY = oneMinusT * oneMinusT * baseY + 2 * oneMinusT * t * controlY + t * t * baseY;
  
  // Arrow direction - determine if jump is positive or negative
  const isPositive = jump.endValue > jump.startValue;
  
  // Calculate tangent angle at the arrow position (t=0.48)
  // For quadratic bezier, derivative: P'(t) = 2(1-t)(P1-P0) + 2t(P2-P1)
  // At t=0.48: P'(0.48) = 2(0.52)(P1-P0) + 2(0.48)(P2-P1)
  // When jump is negative, the curve direction reverses, so we need to account for that
  let arrowAngle = 0;
  if (isHorizontal) {
    // Calculate tangent vector at t=0.48
    const dx1 = midX - startPx; // P1.x - P0.x
    const dy1 = controlY - baseY; // P1.y - P0.y
    const dx2 = endPx - midX; // P2.x - P1.x
    const dy2 = baseY - controlY; // P2.y - P1.y
    let dx = 2 * oneMinusT * dx1 + 2 * t * dx2;
    let dy = 2 * oneMinusT * dy1 + 2 * t * dy2;
    
    // If jump is negative, reverse the direction
    if (!isPositive) {
      dx = -dx;
      dy = -dy;
    }
    
    arrowAngle = Math.atan2(dy, dx) * (180 / Math.PI);
  } else {
    // For vertical: swap x and y
    const dy1 = midX - startPx;
    const dx1 = controlY - baseY;
    const dy2 = endPx - midX;
    const dx2 = baseY - controlY;
    let dy = 2 * oneMinusT * dy1 + 2 * t * dy2;
    let dx = 2 * oneMinusT * dx1 + 2 * t * dx2;
    
    // If jump is negative, reverse the direction
    if (!isPositive) {
      dx = -dx;
      dy = -dy;
    }
    
    arrowAngle = Math.atan2(dy, dx) * (180 / Math.PI);
  }
  
  // Drag state
  const isBeingDragged = isJumpDragging ? isJumpDragging(jump.id) : false;
  const isStartDragging = isJumpDragging ? isJumpDragging(jump.id, 'start') : false;
  const isEndDragging = isJumpDragging ? isJumpDragging(jump.id, 'end') : false;
  
  // Label
  const displayLabel = getJumpDisplayLabel(jump, layoutSettings, numberType, locale, fractionDisplay);
  const mathLabel = formatJumpLabelForKaTeX(jump, layoutSettings, numberType, locale, fractionDisplay);
  const hasLineBreak = mathLabel.includes('\\\\');
  const needsKaTeX = displayLabel.includes('/') || displayLabel.includes('\\frac') || displayLabel.includes('×') || displayLabel.includes('\\times') || displayLabel.includes('^{+}') || displayLabel.includes('^{-}') || mathLabel.includes('\\times') || mathLabel.includes('\\frac') || mathLabel.includes('^{+}') || mathLabel.includes('^{-}') || hasLineBreak;
  
  // Split multi-line labels
  const mathLabelParts = hasLineBreak ? mathLabel.split('\\\\') : [mathLabel];
  
  // Check if label has any content to display
  // Remove HTML tags and LaTeX commands to check for actual content
  const cleanDisplayLabel = displayLabel.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  const cleanMathLabel = mathLabel.replace(/\\[a-zA-Z]+\{?[^}]*\}?/g, '').replace(/\{|\}/g, '').trim();
  const hasLabelContent = cleanDisplayLabel.length > 0 || cleanMathLabel.length > 0;
  
  // Check if label is visible (default to true if not set)
  const isLabelVisible = jump.labelVisible !== false;
  
  // Calculate approximate label width based on content
  // Estimate: ~8px per character for math, add padding
  // Scale width with text scale
  const baseFontSize = 15; // Base font size (x1)
  const fontSize = baseFontSize * textScale;
  const labelTextLength = needsKaTeX 
    ? mathLabel.replace(/\\[a-zA-Z]+|\{|\}/g, '').length 
    : displayLabel.length;
  const estimatedWidth = Math.max(120 * textScale, labelTextLength * 8 * textScale + 40 * textScale);
  
  // Label position
  const labelX = (startPx + endPx) / 2;
  // Increase offset for multi-line labels to prevent overlap with curve
  // Scale offset with text size to prevent covering arrow at larger sizes
  const baseLabelOffset = hasLineBreak ? LABEL_OFFSET_Y + 20 : LABEL_OFFSET_Y;
  // Add extra offset for larger text sizes (minimum 5px extra per scale unit above 1)
  const extraOffsetForScale = Math.max(0, (textScale - 1) * 8);
  const legacyLabelOffset = baseLabelOffset * textScale + extraOffsetForScale;
  
  // Calculate safe offset based on visual peak (arcHeight * 0.5) to avoid overlap
  // For chained jumps (low arc), the legacy offset (relative to control point) puts the label too low
  const boxHalfHeight = (hasLineBreak ? 26 : 16) * textScale;
  const peakClearance = 8 * textScale;
  const safeLabelOffset = (arcHeight * 0.5) + boxHalfHeight + peakClearance;
  
  // Use the larger of the two total offsets from the line
  const totalLegacyOffset = arcHeight + legacyLabelOffset;
  const finalOffset = Math.max(totalLegacyOffset, safeLabelOffset);

  const labelY = isHorizontal 
    ? baseY - finalOffset
    : baseY + finalOffset;
  
  // Handle drag start
  const handleStartDrag = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    if (onDragStart) {
      onDragStart(e, jump.id, 'start');
    }
  }, [onDragStart, jump.id]);
  
  const handleEndDrag = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    if (onDragStart) {
      onDragStart(e, jump.id, 'end');
    }
  }, [onDragStart, jump.id]);
  
  const handleWholeDrag = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDragStart) {
      onDragStart(e, jump.id, 'whole');
    }
  }, [onDragStart, jump.id]);
  
  const handleArcMouseDown = useCallback((e: React.MouseEvent) => {
    // Check if click is far enough from handles to trigger whole jump drag
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const startHandleX = Math.abs(startPx - clickX);
    const endHandleX = Math.abs(endPx - clickX);
    
    // If click is more than 25px away from both handles, drag the whole jump
    if (startHandleX > 25 && endHandleX > 25) {
      handleWholeDrag(e);
    }
  }, [startPx, endPx, handleWholeDrag]);
  
  const handleJumpClickWithDragCheck = useCallback((e: React.MouseEvent) => {
    // Only trigger click if not dragging
    if (!isBeingDragged) {
      onClick(jump.id, e);
    }
  }, [onClick, jump.id, isBeingDragged]);
  
  const isWholeDragging = isJumpDragging ? isJumpDragging(jump.id, 'whole') : false;
  
  return (
    <g
      className={`jump-indicator ${jump.selected ? 'selected' : ''} ${isInChain ? 'in-chain' : ''}`}
      onClick={handleJumpClickWithDragCheck}
      style={{ cursor: 'pointer' }}
    >
      {/* Selection highlight */}
      {jump.selected && (
        <rect
          x={Math.min(startPx, endPx) - 5}
          y={baseY - arcHeight - 20}
          width={width + 10}
          height={arcHeight + 30}
          fill={SELECTION_COLOR}
          fillOpacity={0.1}
          stroke={SELECTION_COLOR}
          strokeWidth={2}
          strokeDasharray="4,2"
          rx={4}
        />
      )}
      
      {/* Main arc (visible) */}
      <path
        d={arcPath}
        fill="none"
        stroke={jump.color}
        strokeWidth={2.5}
        strokeLinecap="round"
        style={{ pointerEvents: 'none' }}
      />
      
      {/* Invisible wider click area for whole jump dragging (rendered after visible path to be on top) */}
      <path
        d={arcPath}
        stroke="transparent"
        strokeWidth={12}
        fill="none"
        strokeLinecap="round"
        style={{
          cursor: isWholeDragging ? 'grabbing' : 'pointer',
          pointerEvents: 'auto'
        }}
        onMouseDown={handleArcMouseDown}
        onTouchStart={handleWholeDrag}
        onClick={(e) => {
          if (!isBeingDragged) {
            handleJumpClickWithDragCheck(e);
          }
        }}
        className="jump-path-click-area"
      />
      
      {/* Arrowhead (only if not zero-length) - positioned at middle of curve */}
      {!isZeroLength && (
        <g transform={isHorizontal ? `rotate(${arrowAngle} ${midX} ${midY})` : `rotate(${arrowAngle} ${midY} ${midX})`}>
          <Arrowhead
            x={isHorizontal ? midX : midY}
            y={isHorizontal ? midY : midX}
            direction={isPositive ? 'right' : 'left'}
            color={jump.color}
            isHorizontal={isHorizontal}
            onMouseDown={handleEndDrag}
            onTouchStart={handleEndDrag}
            onClick={(e) => {
              e.stopPropagation();
              if (!isBeingDragged) {
                onClick(jump.id, e);
              }
            }}
            isDragging={isEndDragging}
          />
        </g>
      )}
      
      {/* Start handle */}
      <circle
        cx={startPx}
        cy={baseY}
        r={isStartDragging ? HANDLE_RADIUS + 2 : HANDLE_RADIUS}
        fill={isDark ? '#1f2937' : '#ffffff'}
        stroke={jump.color}
        strokeWidth={2}
        className="jump-handle start-handle"
        style={{
          cursor: onDragStart ? getDragCursor(isStartDragging, 'start', isHorizontal) : 'pointer',
          filter: isStartDragging ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' : 'none'
        }}
        onMouseDown={handleStartDrag}
        onTouchStart={handleStartDrag}
        onClick={(e) => {
          e.stopPropagation();
          if (!isBeingDragged) {
            onClick(jump.id, e);
          }
        }}
      />
      
      {/* End handle (larger if zero-length) */}
      <circle
        cx={endPx}
        cy={baseY}
        r={isEndDragging ? HANDLE_RADIUS + 4 : (isZeroLength ? HANDLE_RADIUS + 2 : HANDLE_RADIUS)}
        fill={isDark ? '#1f2937' : '#ffffff'}
        stroke={jump.color}
        strokeWidth={2}
        className="jump-handle end-handle"
        style={{
          cursor: onDragStart ? getDragCursor(isEndDragging, 'end', isHorizontal) : 'pointer',
          filter: isEndDragging ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' : 'none'
        }}
        onMouseDown={handleEndDrag}
        onTouchStart={handleEndDrag}
        onClick={(e) => {
          e.stopPropagation();
          if (!isBeingDragged) {
            onClick(jump.id, e);
          }
        }}
      />
      
      {/* Label box - show if there's content OR if label is hidden (to indicate hidden label) */}
      {(hasLabelContent || !isLabelVisible) && (
        <foreignObject
          x={labelX - estimatedWidth / 2}
          y={labelY - (hasLineBreak ? 24 * textScale : 16 * textScale)}
          width={estimatedWidth}
          height={hasLineBreak ? 52 * textScale : 32 * textScale}
          style={{ overflow: 'visible', cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            dispatch(toggleJumpLabelVisibility(jump.id));
          }}
          title={!isLabelVisible ? 'Click to show label' : 'Click to hide label'}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              background: isDark ? '#1f2937' : '#ffffff',
              borderRadius: `${4 * textScale}px`,
              padding: `${4 * textScale}px ${10 * textScale}px`,
              fontSize: `${fontSize}px`,
              fontWeight: 500,
              color: jump.color,
              border: `1px solid ${jump.color}40`,
              whiteSpace: 'nowrap',
              lineHeight: '1.3',
              minWidth: 'fit-content',
              minHeight: `${hasLineBreak ? 52 * textScale : 32 * textScale}px`,
              opacity: !isLabelVisible ? 0.5 : 1,
            }}
          >
            {isLabelVisible ? (
              needsKaTeX ? (
                hasLineBreak ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                    {mathLabelParts.map((part, idx) => (
                      <MathRenderer key={idx} expression={part.trim()} />
                    ))}
                  </div>
                ) : (
                  <MathRenderer expression={mathLabel} />
                )
              ) : (
                <span dangerouslySetInnerHTML={{ __html: displayLabel.replace(/\n/g, '<br/>') }} />
              )
            ) : (
              <span style={{ opacity: 0.3 }}>⋯</span>
            )}
          </div>
        </foreignObject>
      )}
      
      {/* Chain position indicator (if in chain) */}
      {isInChain && chainPosition && layoutSettings.showChainIndicators && (
        <text
          x={labelX}
          y={labelY + 18}
          textAnchor="middle"
          fontSize={9}
          fill={CHAIN_INDICATOR_COLOR}
          opacity={0.7}
        >
          {chainPosition.positionInChain + 1}/{chainPosition.chainLength}
        </text>
      )}
    </g>
  );
};

// =============================================================================
// ARROWHEAD
// =============================================================================

interface ArrowheadProps {
  x: number;
  y: number;
  direction: 'left' | 'right';
  color: string;
  isHorizontal: boolean;
  onMouseDown?: (e: React.MouseEvent) => void;
  onTouchStart?: (e: React.TouchEvent) => void;
  onClick?: (e: React.MouseEvent) => void;
  isDragging?: boolean;
}

const Arrowhead: React.FC<ArrowheadProps> = ({
  x,
  y,
  direction,
  color,
  isHorizontal,
  onMouseDown,
  onTouchStart,
  onClick,
  isDragging = false,
}) => {
  const size = ARROWHEAD_SIZE;
  
  let path: string;
  if (isHorizontal) {
    if (direction === 'right') {
      path = `M ${x - size} ${y - size/2} L ${x} ${y} L ${x - size} ${y + size/2}`;
    } else {
      path = `M ${x + size} ${y - size/2} L ${x} ${y} L ${x + size} ${y + size/2}`;
    }
  } else {
    // Vertical orientation
    if (direction === 'right') {
      path = `M ${x - size/2} ${y - size} L ${x} ${y} L ${x + size/2} ${y - size}`;
    } else {
      path = `M ${x - size/2} ${y + size} L ${x} ${y} L ${x + size/2} ${y + size}`;
    }
  }
  
  return (
    <path
      d={path}
      fill="none"
      stroke={color}
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="jump-handle end-handle"
      style={{
        cursor: onMouseDown ? getDragCursor(isDragging, 'end', isHorizontal) : 'pointer',
        filter: isDragging ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' : 'none'
      }}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      onClick={onClick}
    />
  );
};

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Create SVG path for jump arc
 */
function createJumpArcPath(
  startX: number,
  endX: number,
  baseY: number,
  height: number,
  isHorizontal: boolean,
  isZeroLength: boolean
): string {
  if (isZeroLength) {
    // Just a point indicator for zero-length jumps
    return `M ${startX} ${baseY}`;
  }
  
  const midX = (startX + endX) / 2;
  const controlY = baseY - height;
  
  if (isHorizontal) {
    // Quadratic bezier curve
    return `M ${startX} ${baseY} Q ${midX} ${controlY} ${endX} ${baseY}`;
  } else {
    // For vertical, swap X and Y
    return `M ${baseY} ${startX} Q ${controlY} ${midX} ${baseY} ${endX}`;
  }
}

export default JumpIndicatorsRenderer;
